import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { LuClock } from "react-icons/lu";
import Seo from "../components/Seo";
import DownloadButton from "../components/DownloadButton";
import { useMarkets, useNow, useResults } from "../hooks/useData";
import { BRAND, siteUrl } from "../utils/config";
import { latestDay, resultString, titleCase } from "../utils/data";

const FAQ = [
  {
    q: "Where can I see the satta matka result today?",
    a: "This page lists the result of every market for today. Each row shows the open pana, jodi and close pana as soon as they are declared. Until then it shows XXX - XX - XXX.",
  },
  {
    q: "Why does a market show XXX - XX - XXX?",
    a: "It means that market has not declared today's result yet. The numbers appear automatically when the market declares.",
  },
  {
    q: "How often is the matka result updated?",
    a: "Results are loaded from the live results service every time you open the page, and the page refreshes the data about once a minute.",
  },
];

const ResultToday = () => {
  const { markets, loading } = useMarkets();
  const { bySlug } = useResults();
  const now = useNow();
  const today = now.format("YYYY-MM-DD");
  const origin = siteUrl();

  const rows = useMemo(
    () =>
      markets.map((m) => {
        const days = bySlug[m.slug] || {};
        return { ...m, today: days[today] || null, last: latestDay(days) };
      }),
    [markets, bySlug, today]
  );
  const declared = rows.filter((r) => r.today).length;

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${origin}/` },
        { "@type": "ListItem", position: 2, name: "Matka Result Today", item: `${origin}/matka-result-today` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  return (
    <main className="rt-main">
      <Seo
        title={`Satta Matka Result Today – ${now.format("DD MMM YYYY")} Live Matka Result | ${BRAND.name}`}
        description={`Satta matka result today (${now.format("DD MMMM YYYY")}) for Kalyan, Milan, Madhur, Sridevi, Main Bazar, Rajdhani, Time Bazar and more — open pana, jodi and close pana, updated live.`}
        path="/matka-result-today"
        schema={schema}
      />

      <section className="rt-phead">
        <div className="rt-wrap">
          <nav className="rt-crumbs" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span>/</span>
            <b>Matka Result Today</b>
          </nav>
          <span className="rt-eyebrow rt-eyebrow--dark">{now.format("dddd, DD MMM YYYY")}</span>
          <h1>Satta Matka Result Today</h1>
          <p>
            Live matka result of every market for today. {declared} of {rows.length || "all"} markets have declared so far.
          </p>
        </div>
      </section>

      <section className="rt-wrap rt-section">
        <div className="rt-tablewrap rt-tablewrap--flat">
          <table className="rt-table rt-table--list">
            <caption className="rt-sr">Matka result today for every market</caption>
            <thead>
              <tr>
                <th scope="col">Market</th>
                <th scope="col">Timing</th>
                <th scope="col">Today&apos;s result</th>
                <th scope="col">Last declared</th>
                <th scope="col">Charts</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.slug}>
                  <th scope="row" className="rt-table__name">
                    {titleCase(r.name)}
                  </th>
                  <td>
                    <span className="rt-table__time">
                      <LuClock size={13} aria-hidden="true" /> {r.openTime} - {r.closeTime}
                    </span>
                  </td>
                  <td className={r.today ? "rt-live" : "rt-wait"}>{resultString(r.today)}</td>
                  <td className="rt-table__last">
                    {r.last ? (
                      <>
                        {resultString(r.last)}
                        <small>{moment(r.last.date).format("DD MMM YYYY")}</small>
                      </>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>
                    <span className="rt-table__links">
                      <Link to={`/jodi/${r.slug}`}>Jodi</Link>
                      <Link to={`/pana/${r.slug}`}>Pana</Link>
                    </span>
                  </td>
                </tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="rt-table__empty">
                    Results are not available right now. Please try again shortly.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="rt-howto">
          <strong>Tip:</strong> XXX - XX - XXX means the market has not declared yet. Tap Jodi or Pana to open the full
          week-by-week chart of any market.
        </p>
        <div className="rt-cta-row">
          <DownloadButton />
        </div>
      </section>

      <section className="rt-section rt-section--tint">
        <div className="rt-wrap rt-prose">
          <h2>Matka Result Today — How This Page Works</h2>
          <p>
            Every market on {BRAND.name} publishes an open pana, a jodi and a close pana. This page collects them in one
            table so you can check the <strong>satta matka result today</strong> without opening each market separately.
            The jodi is made from the last digit of the open pana sum and the last digit of the close pana sum.
          </p>
          <p>
            Want history? Open the <Link to="/charts">All Charts</Link> page, or read the{" "}
            <Link to="/satta-matka">satta matka guide</Link> to learn how Jodi and Pana charts are read.
          </p>
          <h3>Frequently asked questions</h3>
          {FAQ.map((f) => (
            <div key={f.q}>
              <h4>{f.q}</h4>
              <p>{f.a}</p>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
};

export default ResultToday;
